import React from "react";
import EventItem from "./eventItems/EventItem";
import moment from "moment-timezone";
import { groupBy } from "lodash";

/**
 * Functional component that iterates over the events in the data list and renders EventItem
 */
const EventItems = ({
  data,
  meetups,
  container = "",
  addSelectedEvent,
  checkBoxSelection,
  cloneCustomEvent,
  selectAllHandler,
  allSelected,
  selEventIds,
  timezone,
  time_format,
  deleteSelEvent,
  showDeleteEventModal,
  showEditEventModal,
  editCustomEvent,
  favoriteOverrides,
  onToggleFavorite,
}) => {
  const resolveFavorite = (item) => {
    if (
      favoriteOverrides &&
      Object.prototype.hasOwnProperty.call(favoriteOverrides, item.id)
    ) {
      return favoriteOverrides[item.id];
    }
    return item.favorite || false;
  };

  const renderSelectAllCheckBox = () => (
    <div className="d-flex align-items-center">
      <div className="checkbox-wrapper d-flex align-items-center d-block float-left">
        <input
          type="checkbox"
          name="select all checkbox"
          onChange={selectAllHandler}
          checked={allSelected}
        />
        <span className="checkmark" />
      </div>
      <label className="text-left text--gray7 fs-body3 m-0 w-25 d-block float-left">
        SELECT ALL ({selEventIds ? selEventIds.length : 0})
      </label>
    </div>
  );

    // Render a fixture row in Add Events
  const renderFixtureItem = (item) => (
    <EventItem
      {...item}
      meetupId={item.id}
      container={container}
      checkBoxSelection={checkBoxSelection}
      cloneCustomEvent={() => cloneCustomEvent(item)}
      addSelectedEvent={addSelectedEvent}
      selectAllHandler={selectAllHandler}
      allSelected={allSelected}
      selEventIds={selEventIds}
      timezone={timezone}
      time_format={time_format}
      socials={false}
    />
  );

  // Render custom and reservation items in My Schedule
  const renderByType = (item) => {
    if (item.event) {
      return (
        <EventItem
          {...item.event}
          meetupId={item.id}
          container={container}
          checkBoxSelection={checkBoxSelection}
          type="custom"
          stations={item.stations}
          sport_object={item.sport_object}
          addSelectedEvent={addSelectedEvent}
          selectAllHandler={selectAllHandler}
          allSelected={allSelected}
          selEventIds={selEventIds}
          deleteSelEvent={deleteSelEvent}
          showDeleteEventModal={showDeleteEventModal}
          showEditEventModal={showEditEventModal}
          editCustomEvent={editCustomEvent}
          recurring={item.recurring || item.event.recurring}
          event_type={item.event_type || item.event.event_type}
          timezone={timezone}
          time_format={time_format}
          socials={container.includes("myevents")}
          pref_station={item.pref_station}
          favorite={resolveFavorite(item)}
          onToggleFavorite={onToggleFavorite}
        />
      );
    }
    if (item.reservation) {
      return (
        <EventItem
          {...item.reservation}
          meetupId={item.id}
          container={container}
          checkBoxSelection={checkBoxSelection}
          type="reservation"
          deleteSelEvent={deleteSelEvent}
          showDeleteEventModal={showDeleteEventModal}
          showEditEventModal={showEditEventModal}
          editCustomEvent={editCustomEvent}
          recurring={item.recurring}
          event_type={item.event_type}
          addSelectedEvent={addSelectedEvent}
          selectAllHandler={selectAllHandler}
          allSelected={allSelected}
          selEventIds={selEventIds}
          timezone={timezone}
          time_format={time_format}
          socials={container.includes("myevents")}
          pref_station={item.pref_station}
          favorite={resolveFavorite(item)}
          onToggleFavorite={onToggleFavorite}
        />
      );
    }
    if (item.fixture) {
      return (
        <EventItem
          {...item.fixture}
          meetupId={item.id}
          container={container}
          checkBoxSelection={checkBoxSelection}
          type="standard"
          stations={item.fixture.stations}
          sport_object={item.sport_object}
          selectAllHandler={selectAllHandler}
          allSelected={allSelected}
          selEventIds={selEventIds}
          deleteSelEvent={deleteSelEvent}
          showDeleteEventModal={showDeleteEventModal}
          showEditEventModal={showEditEventModal}
          editCustomEvent={editCustomEvent}
          recurring={item.recurring}
          event_type={item.event_type}
          timezone={timezone}
          time_format={time_format}
          socials={container.includes("myevents")}
          pref_station={item.pref_station}
          favorite={resolveFavorite(item)}
          onToggleFavorite={onToggleFavorite}
        />
      );
    }
    return null;
  };

  const groupedEvents = groupBy(
    data.map((evt) => {
      let groupDate = null;
      if (evt.time) {
        groupDate = moment(evt.time).tz(timezone).format("D MMMM YYYY - dddd").toUpperCase();
      } else if (evt.event) {
        groupDate = moment(evt.event.start_time).tz(timezone).format("D MMMM YYYY - dddd").toUpperCase();
      } else if (evt.fixture) {
        groupDate = moment(evt.fixture.time).tz(timezone).format("D MMMM YYYY - dddd").toUpperCase();
      } else if (evt.reservation) {
        groupDate = moment(evt.reservation.start_time).tz(timezone).format("D MMMM YYYY - dddd").toUpperCase();
      }
      return { ...evt, group: groupDate };
    }),
    "group"
  );

  return (
    <div className="col bg-white event-items">
      {/* Header row */}
      <div className="d-flex event-items__header border-top">
        <div className="col-6 p-3">
          {container.includes("events") && renderSelectAllCheckBox()}
        </div>
        <div className="col-1 d-flex justify-content-center align-items-center text--gray7 fs-body3 p-3">
          TIME
        </div>
        <div className="col d-flex justify-content-center align-items-center text--gray7 fs-body3 p-3">
          CHANNELS
        </div>
        {container.includes("myevents") && (
          <div className="col d-flex justify-content-center align-items-center text--gray7 fs-body3 p-3">
            SOCIAL
          </div>
        )}
        {!container.includes("dashboard") && (
          <div className="col-1 d-flex justify-content-center align-items-center text--gray7 fs-body3 p-3">
            ACTIONS
          </div>
        )}
      </div>

      {/* Grouped event rows */}
      {Object.entries(groupedEvents).map(([group, items]) => (
        <div className="event-items__group" key={group}>
          {/* Group header */}
          {items.filter((item) =>
            container === "addevents"
              ? !meetups.some((m) => m.fixture && m.fixture.id === item.id)
              : true
          ).length > 0 && (
            <p
              className={`m-0 border-bottom border-top position-sticky event-items__group-title ${
                container.includes("dashboard")
                  ? "event-items__group-title-dashboard"
                  : "event-items__group-title-events"
              } text--gray7 bg-white pl-5 p-3 fs-body3 pl-2`}
            >
              {group}
            </p>
          )}

          {/* Individual items */}
          {items
            .filter((item) =>
              container === "addevents"
                ? !meetups.some((m) => m.fixture && m.fixture.id === item.id)
                : true
            )
            .map((item, idx) => (
              <div
                className="align-items-center text-nowrap p-0 m-0"
                key={`${container}-${item.id}-${idx}`}
              >
                <div
                  className={`border-bottom d-flex event-item bg-white p-0 m-0${
                    item.published_instagram
                      ? " event-item--published-instagram"
                      : " event-item--unpublished"
                  }${
                    item.published_facebook
                      ? " event-item--published-facebook"
                      : " event-item--unpublished"
                  }${
                    item.published
                      ? " event-item--published-sportcompass"
                      : " event-item--unpublished"
                  }${
                    item.reservation ? " bg-reservation" : " bg-white"
                  }`}
                >
                  {container === "addevents"
                    ? renderFixtureItem(item)
                    : renderByType(item)}
                </div>
              </div>
            ))}
        </div>
      ))}

      <div className="p-3 bg-light">
        <div style={{ height: "40px" }} />
      </div>
    </div>
  );
};

export default EventItems;
